// GSTR-1 sections (gst-parked 070, D8): the section summary the export page
// previews, and the GST portal CSV files — one per section, with the GSTN
// Returns Offline Tool's column headers in its order (the same layout as the
// API's queued export, services/api/src/billing/gstr1-sections.ts).

import type { Gstr1Amounts, Gstr1ReportData, Gstr1Section } from './types';

type Sections = Pick<
  Gstr1ReportData,
  'b2b' | 'b2cl' | 'b2cs' | 'cdnr' | 'cdnur' | 'total_invoices'
>;

const totals = (rows: Gstr1Amounts[]) =>
  rows.reduce(
    (s, r) => ({
      taxable_value: s.taxable_value + Number(r.taxable_value),
      total_tax: s.total_tax + Number(r.cgst) + Number(r.sgst) + Number(r.igst),
    }),
    { taxable_value: 0, total_tax: 0 },
  );

/** One preview row per non-empty section. B2CS counts the B2C invoices not filed as B2CL. */
export function gstr1SectionSummary(data: Sections): Gstr1Section[] {
  const b2csInvoices = Math.max(0, data.total_invoices - data.b2b.length - data.b2cl.length);
  const rows: Gstr1Section[] = [
    {
      key: 'B2B',
      label: 'B2B (registered buyers)',
      invoice_count: data.b2b.length,
      ...totals(data.b2b),
    },
    {
      key: 'B2CL',
      label: 'B2CL (large inter-state)',
      invoice_count: data.b2cl.length,
      ...totals(data.b2cl),
    },
    { key: 'B2CS', label: 'B2CS (other B2C)', invoice_count: b2csInvoices, ...totals(data.b2cs) },
    {
      key: 'CDNR',
      label: 'CDNR (notes, registered)',
      invoice_count: data.cdnr.length,
      ...totals(data.cdnr),
    },
    {
      key: 'CDNUR',
      label: 'CDNUR (notes, unregistered)',
      invoice_count: data.cdnur.length,
      ...totals(data.cdnur),
    },
  ];
  return rows.filter((r) => r.invoice_count > 0 || r.taxable_value !== 0 || r.total_tax !== 0);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-05" → "05-Oct-2026", the portal's date format. */
export function portalDate(isoDay: string | null): string {
  if (!isoDay) return '';
  const [y, m, d] = isoDay.split('-');
  return `${d}-${MONTHS[Number(m) - 1]}-${y}`;
}

export interface Gstr1PortalFile {
  /** Lower-case portal section name: b2b, b2cl, b2cs, cdnr, cdnur. */
  section: string;
  headers: string[];
  rows: Array<Record<string, string | number>>;
}

/**
 * The portal CSV files with rows, in section order. Amounts in major units,
 * unsigned except B2CS (which nets its credit notes); a note's direction is
 * its Note Type.
 */
export function gstr1PortalFiles(data: Sections, subunit: number): Gstr1PortalFile[] {
  const decimals = Math.round(Math.log10(subunit));
  const amt = (v: number) => (Math.abs(v) / subunit).toFixed(decimals);
  const files: Gstr1PortalFile[] = [
    {
      section: 'b2b',
      headers: [
        'GSTIN/UIN of Recipient',
        'Receiver Name',
        'Invoice Number',
        'Invoice date',
        'Invoice Value',
        'Place Of Supply',
        'Reverse Charge',
        'Applicable % of Tax Rate',
        'Invoice Type',
        'E-Commerce GSTIN',
        'Rate',
        'Taxable Value',
        'Cess Amount',
      ],
      rows: data.b2b.flatMap((inv) =>
        inv.rates.map((r) => ({
          'GSTIN/UIN of Recipient': inv.buyer_gstin ?? '',
          'Receiver Name': inv.buyer_name ?? '',
          'Invoice Number': inv.invoice_number,
          'Invoice date': portalDate(inv.invoice_date),
          'Invoice Value': amt(inv.invoice_value),
          'Place Of Supply': inv.place_of_supply_name ?? '',
          'Reverse Charge': 'N',
          'Applicable % of Tax Rate': '',
          'Invoice Type': 'Regular B2B',
          'E-Commerce GSTIN': '',
          Rate: r.rate,
          'Taxable Value': amt(r.taxable_value),
          'Cess Amount': '',
        })),
      ),
    },
    {
      section: 'b2cl',
      headers: [
        'Invoice Number',
        'Invoice date',
        'Invoice Value',
        'Place Of Supply',
        'Applicable % of Tax Rate',
        'Rate',
        'Taxable Value',
        'Cess Amount',
        'E-Commerce GSTIN',
      ],
      rows: data.b2cl.flatMap((inv) =>
        inv.rates.map((r) => ({
          'Invoice Number': inv.invoice_number,
          'Invoice date': portalDate(inv.invoice_date),
          'Invoice Value': amt(inv.invoice_value),
          'Place Of Supply': inv.place_of_supply_name ?? '',
          'Applicable % of Tax Rate': '',
          Rate: r.rate,
          'Taxable Value': amt(r.taxable_value),
          'Cess Amount': '',
          'E-Commerce GSTIN': '',
        })),
      ),
    },
    {
      section: 'b2cs',
      headers: [
        'Type',
        'Place Of Supply',
        'Applicable % of Tax Rate',
        'Rate',
        'Taxable Value',
        'Cess Amount',
        'E-Commerce GSTIN',
      ],
      rows: data.b2cs.map((r) => ({
        Type: 'OE',
        'Place Of Supply': r.place_of_supply_name ?? '',
        'Applicable % of Tax Rate': '',
        Rate: r.rate,
        'Taxable Value': (r.taxable_value / subunit).toFixed(decimals),
        'Cess Amount': '',
        'E-Commerce GSTIN': '',
      })),
    },
    {
      section: 'cdnr',
      headers: [
        'GSTIN/UIN of Recipient',
        'Receiver Name',
        'Note Number',
        'Note Date',
        'Note Type',
        'Place Of Supply',
        'Reverse Charge',
        'Note Supply Type',
        'Note Value',
        'Applicable % of Tax Rate',
        'Rate',
        'Taxable Value',
        'Cess Amount',
      ],
      rows: data.cdnr.flatMap((n) =>
        n.rates.map((r) => ({
          'GSTIN/UIN of Recipient': n.buyer_gstin ?? '',
          'Receiver Name': n.buyer_name ?? '',
          'Note Number': n.note_number,
          'Note Date': portalDate(n.note_date),
          'Note Type': n.note_type,
          'Place Of Supply': n.place_of_supply_name ?? '',
          'Reverse Charge': 'N',
          'Note Supply Type': 'Regular B2B',
          'Note Value': amt(n.note_value),
          'Applicable % of Tax Rate': '',
          Rate: r.rate,
          'Taxable Value': amt(r.taxable_value),
          'Cess Amount': '',
        })),
      ),
    },
    {
      section: 'cdnur',
      headers: [
        'UR Type',
        'Note Number',
        'Note Date',
        'Note Type',
        'Place Of Supply',
        'Note Value',
        'Applicable % of Tax Rate',
        'Rate',
        'Taxable Value',
        'Cess Amount',
      ],
      rows: data.cdnur.flatMap((n) =>
        n.rates.map((r) => ({
          'UR Type': 'B2CL',
          'Note Number': n.note_number,
          'Note Date': portalDate(n.note_date),
          'Note Type': n.note_type,
          'Place Of Supply': n.place_of_supply_name ?? '',
          'Note Value': amt(n.note_value),
          'Applicable % of Tax Rate': '',
          Rate: r.rate,
          'Taxable Value': amt(r.taxable_value),
          'Cess Amount': '',
        })),
      ),
    },
  ];
  return files.filter((f) => f.rows.length > 0);
}
